import { useCallback } from 'react'
import { Nullable } from '../../../common/Nullable'
import { useLogger } from '../../../components/Logger/hooks/useLogger'
import * as Sentry from '@sentry/react'
import { useAudioContext } from './useAudioContext'

export type AudioStreamConstraints = {
  deviceId?: string
  channelCount?: number
  echoCancellation?: boolean
  noiseSuppression?: boolean
}

export const useAudioStream = () => {
  const logger = useLogger()
  const { audioContext, closeAudioContext } = useAudioContext()

  const getSupportedAudioConstraints = useCallback(
    async (constraints: AudioStreamConstraints): Promise<MediaTrackConstraints> => {
      const supported = navigator.mediaDevices.getSupportedConstraints()
      await logger.info(`[useAudioStream] Supported audio constraints:`, JSON.stringify(supported))
      const audio: MediaTrackConstraints = {}
      Object.entries(constraints).forEach(([key, value]) => {
        if (value !== undefined && key in supported) {
          switch (key) {
            case 'deviceId':
              audio.deviceId = { exact: value as string }
              break
            case 'channelCount':
              audio.channelCount = value as ConstrainULong
              break
            case 'echoCancellation':
              audio.echoCancellation = value as ConstrainBoolean
              break
            case 'noiseSuppression':
              audio.noiseSuppression = value as ConstrainBoolean
              break
            default:
              break
          }
        }
      })
      return audio
    },
    [logger],
  )

  const createMediaStream = useCallback(
    async (constraints: AudioStreamConstraints): Promise<Nullable<MediaStream>> => {
      try {
        const audioConstraints = await getSupportedAudioConstraints(constraints)
        await logger.info(`[useAudioStream] Requesting user media with constraints: ${JSON.stringify(audioConstraints)}`)
        const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints })
        await logger.info('[useAudioStream] MediaStream acquired')
        return stream
      } catch (error: unknown) {
        Sentry.captureException(error)
        await logger.error('[useAudioStream] Failed to acquire MediaStream', error)
        return null
      }
    },
    [getSupportedAudioConstraints, logger],
  )

  const getChannelCount = async (stream: MediaStream) => {
    try {
      const audioTrack = stream.getAudioTracks()[0]
      let supportedChannels = null
      if (audioTrack.getCapabilities) {
        const caps = audioTrack.getCapabilities()
        if (caps.channelCount) {
          supportedChannels = caps.channelCount
        }
      }

      if (!supportedChannels && audioContext) {
        const source = audioContext.createMediaStreamSource(stream)
        supportedChannels = source.channelCount
        await closeAudioContext()
      }
      return supportedChannels
    } catch (error: unknown) {
      Sentry.captureException(error)
      await logger.error('[useAudioStream] Error detecting audio channels.', error)
      return 1
    }
  }

  return { createMediaStream, getChannelCount }
}
